class StoreLocator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.tabs = this.querySelector('[data-store-tabs]');
    this.results = this.querySelector('[data-store-results]');
    this.count = this.querySelector('[data-store-count]');
    this.previousButton = this.querySelector('[data-store-previous]');
    this.nextButton = this.querySelector('[data-store-next]');
    this.cards = Array.from(this.querySelectorAll('[data-store-card]'));
    this.labels = {
      storeSingular: this.dataset.storeSingular || 'store',
      storePlural: this.dataset.storePlural || 'stores',
      stateSingular: this.dataset.stateSingular || 'state',
      statePlural: this.dataset.statePlural || 'states',
      fallbackState: this.dataset.fallbackState || 'Other locations',
      fallbackCity: this.dataset.fallbackCity || 'Stores',
    };

    if (!this.tabs || !this.results || this.cards.length === 0) return;

    this.initialized = true;
    this.states = this.groupStores(this.cards);
    this.renderTabs();
    this.updateCount();
    this.selectState(0, false);
    this.bindEvents();
    this.classList.add('store-locator--ready');
  }

  groupStores(cards) {
    const states = new Map();

    cards.forEach((card) => {
      const stateName = card.dataset.state || this.labels.fallbackState;
      const cityName = card.dataset.city || this.labels.fallbackCity;

      if (!states.has(stateName)) states.set(stateName, new Map());
      const cities = states.get(stateName);
      if (!cities.has(cityName)) cities.set(cityName, []);
      cities.get(cityName).push(card);
    });

    return Array.from(states, ([name, cities]) => ({ name, cities }));
  }

  renderTabs() {
    const fragment = document.createDocumentFragment();

    this.states.forEach((state, index) => {
      const storeCount = Array.from(state.cities.values()).reduce((sum, stores) => sum + stores.length, 0);
      const button = document.createElement('button');
      const number = document.createElement('span');

      button.className = 'store-locator__tab';
      button.type = 'button';
      button.setAttribute('role', 'tab');
      button.id = `${this.id}-tab-${index}`;
      button.setAttribute('aria-controls', `${this.id}-panel`);
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.tabIndex = index === 0 ? 0 : -1;
      button.dataset.stateIndex = String(index);
      button.append(document.createTextNode(state.name));

      number.className = 'store-locator__tab-count';
      number.textContent = String(storeCount);
      button.append(number);
      fragment.append(button);
    });

    this.tabs.replaceChildren(fragment);
  }

  updateCount() {
    if (!this.count) return;
    const storeLabel = this.cards.length === 1 ? this.labels.storeSingular : this.labels.storePlural;
    const stateLabel = this.states.length === 1 ? this.labels.stateSingular : this.labels.statePlural;
    this.count.textContent = `${this.cards.length} ${storeLabel} · ${this.states.length} ${stateLabel}`;
  }

  selectState(index, focusTab = true) {
    const state = this.states[index];
    if (!state) return;

    this.tabs.querySelectorAll('[role="tab"]').forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focusTab) tab.focus({ preventScroll: true });
    });

    const fragment = document.createDocumentFragment();
    state.cities.forEach((cards, city) => {
      const group = document.createElement('section');
      const heading = document.createElement('div');
      const title = document.createElement('h2');
      const count = document.createElement('span');
      const grid = document.createElement('div');

      group.className = 'store-locator__city-group';
      heading.className = 'store-locator__city-heading';
      title.textContent = city;
      count.textContent = `${cards.length} ${cards.length === 1 ? this.labels.storeSingular : this.labels.storePlural}`;
      grid.className = 'store-locator__cards';
      cards.forEach((card) => grid.append(card));
      heading.append(title, count);
      group.append(heading, grid);
      fragment.append(group);
    });

    this.results.replaceChildren(fragment);
    this.results.setAttribute('aria-labelledby', `${this.id}-tab-${index}`);
    this.activeIndex = index;
    this.syncArrows();
  }

  bindEvents() {
    this.tabs.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-state-index]');
      if (!tab) return;
      this.selectState(Number(tab.dataset.stateIndex), false);
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });

    this.tabs.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let index = this.activeIndex;
      if (event.key === 'ArrowLeft') index = (index - 1 + this.states.length) % this.states.length;
      if (event.key === 'ArrowRight') index = (index + 1) % this.states.length;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = this.states.length - 1;
      this.selectState(index);
      this.tabs.children[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });

    this.previousButton?.addEventListener('click', () => this.scrollTabs(-1));
    this.nextButton?.addEventListener('click', () => this.scrollTabs(1));
    this.tabs.addEventListener('scroll', () => this.syncArrows(), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.syncArrows());
    this.resizeObserver.observe(this.tabs);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  scrollTabs(direction) {
    this.tabs.scrollBy({ left: direction * Math.max(160, this.tabs.clientWidth * 0.7), behavior: 'smooth' });
  }

  syncArrows() {
    if (!this.previousButton || !this.nextButton) return;
    const maximum = this.tabs.scrollWidth - this.tabs.clientWidth;
    this.previousButton.disabled = this.tabs.scrollLeft <= 1;
    this.nextButton.disabled = this.tabs.scrollLeft >= maximum - 1;
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
